/* app/ScreenBoundary.jsx — граница ленивого раздела (kitchen-ux follow-up 30).

   Suspense со СКЕЛЕТОНОМ раздела, пока грузится его чанк, — не пустой экран: та же шапка
   (PageHeader: заголовок, гамбургер на планшете, колокол), что нарисует раздел, и каркас тела.
   Онбординг-туры целятся в .section-body и data-onb раздела: подсветка следит за целью каждый
   кадр, поэтому каркас держит место, а настоящие цели подхватываются, как только чанк пришёл.

   Сбой загрузки чанка (lazyScreen помечает его chunkLoadError) — «Раздел не загрузился» с кнопкой
   и одна автоматическая перезагрузка (lib/chunkReload.js: после выката вкладка держит старый
   index.html, а его чанков на сервере уже нет). ПРОЧИЕ ошибки рендера граница не глотает —
   пробрасывает выше, как было до разбиения на чанки.

   Граница — своя у каждого экрана (key = screenId): роутер-шим рендерит маршруты без ключей, и
   разделы с одинаковой обёрткой делили бы один экземпляр — сбой чанка одного раздела «залипал» бы
   на уже загруженном соседнем, а прежний раздел (вместе с порталами drawer) оставался бы
   смонтированным под скелетоном, пока грузится следующий. Тот же экран на другом адресе
   (/chat → /chat/<id>) границу сохраняет — экран не перемонтируется, как и до разбиения.

   kind: 'section' (по умолчанию; внутри AppShell) — шапка + каркас, body 'rows' | 'grid';
         'chat' — каркас ленты Ассистента; 'bare' — страница вне оболочки (витрина, лендинг курса,
         оплата): без шапки, у неё нет AppShell. */

import React from 'react';
import { PageHeader } from '../components/patterns/PageHeader.jsx';
import { SkeletonGrid } from '../components/patterns/Cards.jsx';
import { ErrorState } from '../components/patterns/States.jsx';
import { Icon } from '../components/ui/Icon.jsx';
import { reloadForFreshBuild } from '../lib/chunkReload.js';
import { useShell } from './layouts/AppShell.jsx';
import { isChunkLoadError } from './lazyScreen.js';

  const { useEffect, useState } = React;

  const ROW_WIDTHS = ['100%', '100%', '100%', '100%', '100%', '100%', '100%', '72%'];

  function SkeletonRows() {
    return (
      <div className="route-skel" aria-hidden="true">
        <div className="route-skel__tools">
          <span className="skeleton route-skel__tool route-skel__tool--search" />
          <span className="skeleton route-skel__tool" />
          <span className="skeleton route-skel__tool" />
        </div>
        <div className="route-skel__rows">
          {ROW_WIDTHS.map((w, i) => <span key={i} className="skeleton route-skel__row" style={{ width: w }} />)}
        </div>
      </div>
    );
  }

  function LoadingNote() {
    return <span className="visually-hidden" role="status">Загружаем раздел…</span>;
  }

  function SectionFallback({ title, subtitle, breadcrumbs, body }) {
    return (
      <div className="section-page" aria-busy="true">
        <PageHeader title={title} subtitle={subtitle} breadcrumbs={breadcrumbs} />
        <div className="section-body">
          <LoadingNote />
          {body === 'grid' ? <SkeletonGrid count={6} /> : <SkeletonRows />}
        </div>
      </div>
    );
  }

  // шапка Ассистента: на планшете — его гамбургер (на 720–1199 px навигация живёт только в нём)
  function ChatTopBar() {
    const shell = useShell();
    return (
      <header className="chat-header">
        {shell.showHamburger && (
          <div className="chat-header__nav">
            <button className="chat-header__icon" onClick={shell.openNav} aria-label="Меню и диалоги">
              <Icon name="menu" size={20} />
            </button>
          </div>
        )}
        <div className="chat-header__titles">
          <div className="chat-header__title-row"><h1 className="chat-header__title t-h3 truncate"><span className="argus-name">Аргус</span></h1></div>
        </div>
      </header>
    );
  }

  // каркас Ассистента: шапка чата + лента, как ChatLoadingSkeleton
  function ChatFallback() {
    return (
      <div className="chat-shell" aria-busy="true">
        <section className="chat-shell__main">
          <ChatTopBar />
          <div className="chat-conv">
            <div className="chat-conv__body">
              <LoadingNote />
              <div className="chat-skeleton" aria-hidden="true">
                <div className="chat-skeleton__row chat-skeleton__row--user"><span className="chat-skeleton__bubble" style={{ width: '46%' }} /></div>
                <div className="chat-skeleton__row">
                  <span className="chat-skeleton__avatar" />
                  <span className="chat-skeleton__stack">
                    <span className="chat-skeleton__line" style={{ width: '72%' }} />
                    <span className="chat-skeleton__line" style={{ width: '88%' }} />
                    <span className="chat-skeleton__line" style={{ width: '54%' }} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  function BareFallback() {
    return (
      <div className="section-page" aria-busy="true">
        <div className="section-body">
          <LoadingNote />
          <span className="skeleton route-skel__title" aria-hidden="true" />
          <SkeletonRows />
        </div>
      </div>
    );
  }

  // текст «нет сети» живой: вернулась сеть — подсказка сменилась без перезагрузки
  function useOnline() {
    const read = () => typeof navigator === 'undefined' || navigator.onLine !== false;
    const [online, setOnline] = useState(read);
    useEffect(() => {
      const sync = () => setOnline(read());
      window.addEventListener('online', sync);
      window.addEventListener('offline', sync);
      return () => { window.removeEventListener('online', sync); window.removeEventListener('offline', sync); };
    }, []);
    return online;
  }

  /* Состояние «Раздел не загрузился» — с той же шапкой, что у каркаса: на планшете гамбургер есть только
     в ней, и без шапки из сбойного раздела было бы не уйти (только «Повторить» или «назад» браузера). */
  function ChunkErrorState({ header }) {
    const online = useOnline();
    const [offlineAtFailure] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);
    let body = 'Скорее всего, вышла новая версия Ресториума. Нажмите «Повторить» — страница обновится и загрузит её.';
    if (!online) body = 'Нет подключения к интернету. Проверьте сеть и нажмите «Повторить».';
    else if (offlineAtFailure) body = 'Связь восстановилась. Нажмите «Повторить», чтобы загрузить раздел.';
    const error = (
      <div className="section-body" role="alert">
        <ErrorState title="Раздел не загрузился" body={body} onRetry={() => window.location.reload()} />
      </div>
    );
    if (header && header.kind === 'chat') {
      return <div className="chat-shell"><section className="chat-shell__main"><ChatTopBar />{error}</section></div>;
    }
    return (
      <div className="section-page">
        {header && <PageHeader title={header.title} subtitle={header.subtitle} breadcrumbs={header.breadcrumbs} />}
        {error}
      </div>
    );
  }

  class ChunkErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
      return { error };
    }

    componentDidCatch(error) {
      if (isChunkLoadError(error)) reloadForFreshBuild();
    }

    render() {
      const { error } = this.state;
      if (!error) return this.props.children;
      // не сбой загрузки — не наша забота: выше, как до разбиения на чанки
      if (!isChunkLoadError(error)) throw error;
      return <ChunkErrorState header={this.props.header} />;
    }
  }

  // ключ границы — экран (lazyScreen.screenId); у не-ленивого содержимого ключа нет
  function screenKeyOf(children) {
    const type = React.isValidElement(children) ? children.type : null;
    return type && type.screenId ? type.screenId : undefined;
  }

  function ScreenBoundary({ kind = 'section', title, subtitle, breadcrumbs, body = 'rows', children }) {
    let fallback;
    if (kind === 'chat') fallback = <ChatFallback />;
    else if (kind === 'bare') fallback = <BareFallback />;
    else fallback = <SectionFallback title={title} subtitle={subtitle} breadcrumbs={breadcrumbs} body={body} />;
    // у страницы вне оболочки шапки раздела нет (и навигации тоже)
    const header = kind === 'bare' ? null : { kind, title, subtitle, breadcrumbs };
    return (
      <ChunkErrorBoundary key={screenKeyOf(children)} header={header}>
        <React.Suspense fallback={fallback}>{children}</React.Suspense>
      </ChunkErrorBoundary>
    );
  }

export { ScreenBoundary, ChunkErrorBoundary, screenKeyOf };
